import axios from 'axios';
import api from '../lib/api';
import { useMutation, useQuery } from '@tanstack/react-query';

// Separate unauthenticated axios instance for public flappy endpoints.
// Does not go through the auth interceptor in lib/api.ts.
const flappyApi = axios.create({
  baseURL: process.env.API_URL,
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
});

export interface ScoreEntry {
  displayName: string;
  score: number;
}

export interface SubmitScorePayload {
  recaptchaToken: string;
  score: number;
  email: string;
  sessionToken: string;
}

export interface SubmitScoreResult {
  displayName: string;
  score: number;
}

export async function createSession(): Promise<{ sessionToken: string }> {
  const res = await flappyApi.post<{ data: { sessionToken: string } }>(
    '/api/base/flappy/session',
  );
  return res.data.data;
}

export function useSubmitScore() {
  return useMutation({
    mutationFn: async (
      payload: SubmitScorePayload,
    ): Promise<SubmitScoreResult> => {
      const res = await flappyApi.post<{ data: SubmitScoreResult }>(
        '/api/base/flappy/scores',
        payload,
      );
      return res.data.data;
    },
  });
}

export function useDeleteScore() {
  return useMutation({
    mutationFn: async (payload: {
      displayName: string;
      score: number;
    }): Promise<void> => {
      await api.delete('/api/base/flappy/scores', { data: payload });
    },
  });
}

// Authenticated endpoint — uses the main api instance which injects the user's JWT.
// The booth display must be on an authenticated Plerion session for this to work.
export function useGetScores(enabled = true) {
  return useQuery({
    queryKey: ['flappy-scores'],
    queryFn: async (): Promise<ScoreEntry[]> => {
      const res = await api.get<{ data: { scores: ScoreEntry[] } }>(
        '/api/base/flappy/scores',
      );
      return res.data.data.scores;
    },
    refetchInterval: 30_000,
    enabled,
  });
}
